import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
} from '@mui/material';
import { Add as AddIcon, CreditCard as CreditCardIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { firmAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import StripePaymentDialog from '../components/StripePaymentDialog';

const ClientFirmWallet: React.FC = () => {
  const { firmId } = useParams<{ firmId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState<any>(null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [depositDialog, setDepositDialog] = useState(false);
  const [onlinePaymentDialog, setOnlinePaymentDialog] = useState(false);
  const [stripePaymentDialog, setStripePaymentDialog] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [depositDescription, setDepositDescription] = useState('');
  const [onlinePaymentAmount, setOnlinePaymentAmount] = useState('');
  const [onlinePaymentDescription, setOnlinePaymentDescription] = useState('');
  const [onlinePaymentMethod, setOnlinePaymentMethod] = useState<'card' | 'wire'>('card');
  const [submitting, setSubmitting] = useState(false);
  const [stripePublishableKey, setStripePublishableKey] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [firmDetails, setFirmDetails] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [savedPaymentMethods, setSavedPaymentMethods] = useState<any[]>([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
  const [useNewCard, setUseNewCard] = useState(false);
  const [showWireBankDetails, setShowWireBankDetails] = useState(false);
  const [wireTransferDialog, setWireTransferDialog] = useState(false);

  // Bank account details for wire transfer
  const wireBankDetails = {
    recipientName: 'IAppel Solutions LLP',
    routingNumber: 'NA',
    accountNumber: '9999 1050 0179 62',
    bankName: 'HDFC Bank',
    swiftCode: 'HDFCINBBXXX',
  };

  const isPmpAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'pmp_admin');
  };

  const isSiteAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'site_admin');
  };

  const isPmpAdminOrSiteAdmin = () => {
    return isPmpAdmin() || isSiteAdmin();
  };

  const isFinanceUser = () => {
    if (!user) return false;
    // Check for finance user role or is_finance_user flag
    const hasFinanceRole = user.roles?.some(role => role.slug === 'client_finance_user');
    const isFinanceFlag = (user as any).is_finance_user === true;
    return hasFinanceRole || isFinanceFlag;
  };

  const canAddDeposit = () => {
    return isPmpAdminOrSiteAdmin() || (isFinanceUser() && (user?.firm_id === parseInt(firmId!) || user?.firm?.id === parseInt(firmId!)));
  };

  const hasAccess = () => {
    return isPmpAdminOrSiteAdmin() || user?.firm_id === parseInt(firmId!) || user?.firm?.id === parseInt(firmId!);
  };

  useEffect(() => {
    if (!hasAccess()) {
      setError('You do not have access to this wallet');
      setLoading(false);
      return;
    }
    fetchWalletData();
    fetchStripeKey();
    fetchSavedPaymentMethods();
  }, [firmId, page]);

  useEffect(() => {
    // Refresh saved payment methods when payment dialog opens
    if (onlinePaymentDialog && stripePublishableKey) {
      fetchSavedPaymentMethods();
    }
  }, [onlinePaymentDialog]);

  const fetchSavedPaymentMethods = async () => {
    try {
      const response = await firmAPI.getPaymentMethods(parseInt(firmId!));
      if (response.data.success) {
        setSavedPaymentMethods(response.data.data || []);
      }
    } catch (err: any) {
      // Payment methods might not exist yet, that's okay
    }
  };

  useEffect(() => {
    console.log('Stripe Init:', stripePublishableKey ? 'Set' : 'Not set');
  }, [stripePublishableKey]);

  const fetchStripeKey = async () => {
    try {
      const response = await firmAPI.getStripePublishableKey();
      // console.log('Stripe key response:', response.data);
      if (response.data.success) {
        // Handle both possible response structures
        const key = response.data.data?.publishable_key || response.data.publishable_key || response.data.data?.publishable;
        if (key) {
          setStripePublishableKey(key);
          // console.log('Stripe publishable key set:', key.substring(0, 20) + '...');
        } else {
          // console.warn('Stripe publishable key not found in response:', response.data);
        }
      } else {
        // console.warn('Stripe key fetch failed:', response.data.message);
      }
    } catch (err: any) {
      // Stripe might not be configured, that's okay
      // console.log('Stripe not configured or unavailable:', err.response?.data || err.message);
    }
  };

  const fetchWalletData = async () => {
    try {
      setLoading(true);
      const [balanceRes, transactionsRes, firmRes] = await Promise.all([
        firmAPI.getWalletBalance(parseInt(firmId!)),
        firmAPI.getWalletTransactions(parseInt(firmId!), { page }),
        firmAPI.getById(parseInt(firmId!)),
      ]);
      
      if (balanceRes.data.success) {
        setBalance(balanceRes.data.data);
      }
      
      if (transactionsRes.data.success) {
        setTransactions(transactionsRes.data.data.data || []);
        setTotalPages(transactionsRes.data.data.last_page || 1);
      }

      if (firmRes.data.success) {
        setFirmDetails(firmRes.data.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch wallet data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDeposit = async () => {
    if (!depositAmount || parseFloat(depositAmount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      const response = await firmAPI.addDeposit(parseInt(firmId!), {
        amount: parseFloat(depositAmount),
        description: depositDescription || 'Cash deposit',
      });
      
      if (response.data.success) {
        setSuccess('Deposit added successfully');
        setDepositDialog(false);
        setDepositAmount('');
        setDepositDescription('');
        fetchWalletData();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add deposit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWireTransfer = async () => {
    if (!showWireBankDetails) {
      setShowWireBankDetails(true);
      return;
    }

    if (!onlinePaymentAmount || parseFloat(onlinePaymentAmount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      const response = await firmAPI.addDeposit(parseInt(firmId!), {
        amount: parseFloat(onlinePaymentAmount),
        description: onlinePaymentDescription || 'Wire transfer deposit',
      });

      if (response.data.success) {
        setSuccess('Wire transfer payment recorded successfully! Funds have been added to your wallet.');
        setWireTransferDialog(false);
        setOnlinePaymentAmount('');
        setOnlinePaymentDescription('');
        setShowWireBankDetails(false);
        await fetchWalletData();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to record wire payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOnlinePayment = async () => {
    if (!onlinePaymentAmount || parseFloat(onlinePaymentAmount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    // Handle card payment (Stripe flow)
    if (!stripePublishableKey) {
      setError('Stripe is not configured. Please use cash deposit instead.');
      return;
    }

    // If using saved card, validate selection (only if there are saved cards)
    if (onlinePaymentMethod === 'card' && savedPaymentMethods.length > 0 && !useNewCard && !selectedPaymentMethodId) {
      setError('Please select a payment method or choose to add a new card');
      return;
    }

    // If no saved cards, default to using new card
    if (onlinePaymentMethod === 'card' && savedPaymentMethods.length === 0) {
      setUseNewCard(true);
    }

    try {
      setSubmitting(true);
      setError('');
      const paymentData: any = {
        amount: parseFloat(onlinePaymentAmount),
        description: onlinePaymentDescription || 'Wallet deposit via Stripe',
        payment_method_type: 'card',
      };

      // Add payment method ID if using saved card
      if (!useNewCard && selectedPaymentMethodId) {
        paymentData.payment_method_id = selectedPaymentMethodId;
      }

      const response = await firmAPI.createStripePaymentIntent(parseInt(firmId!), paymentData);
      
      if (response.data.success) {
        // If using saved card and payment succeeded, handle immediately
        if (!useNewCard && selectedPaymentMethodId && response.data.data.status === 'succeeded') {
          setSuccess('Payment processed successfully! Funds have been added to your wallet.');
          setOnlinePaymentDialog(false);
          setOnlinePaymentAmount('');
          setOnlinePaymentDescription('');
          setOnlinePaymentMethod('card');
          setSelectedPaymentMethodId(null);
          setUseNewCard(false);
          await fetchWalletData();
        } else {
          // Show card input dialog for new card
          setClientSecret(response.data.data.client_secret);
          setPaymentIntentId(response.data.data.payment_intent_id);
          setStripePaymentDialog(true);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStripePaymentSuccess = async (paymentIntentId: string) => {
    try {
      // Immediately confirm payment and add funds to wallet
      const response = await firmAPI.confirmStripePayment(parseInt(firmId!), {
        payment_intent_id: paymentIntentId,
      });

      if (response.data.success) {
        setSuccess('Payment processed successfully! Funds have been added to your wallet.');
        // Refresh wallet data immediately
        await fetchWalletData();
      } else {
        setError(response.data.message || 'Payment confirmed but failed to add funds. Please contact support.');
      }
    } catch (err: any) {
      // Payment succeeded but confirmation failed - webhook will handle it
      setSuccess('Payment processed successfully! Funds will be added to wallet shortly.');
      // Refresh wallet data after a delay to allow webhook to process
      setTimeout(() => {
        fetchWalletData();
      }, 3000);
    } finally {
      setStripePaymentDialog(false);
      setOnlinePaymentDialog(false);
      setOnlinePaymentAmount('');
      setOnlinePaymentDescription('');
      setOnlinePaymentMethod('card');
      setClientSecret(null);
      setPaymentIntentId(null);
      setSelectedPaymentMethodId(null);
      setUseNewCard(false);
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'deposit':
        return 'success';
      case 'allocation':
        return 'error';
      case 'refund':
        return 'info';
      default:
        return 'default';
    }
  };

  if (!hasAccess()) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">You do not have access to this wallet</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link color="inherit" href="#" onClick={() => navigate('/firms')}>
          Firms
        </Link>
        <Link color="inherit" href="#" onClick={() => navigate(`/firms/${firmId}`)}>
          Firm Details
        </Link>
        <Typography color="text.primary">Wallet</Typography>
      </Breadcrumbs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 4, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">Wallet Balance</Typography>
          {canAddDeposit() && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setDepositDialog(true)}
              >
                Add Cash Deposit
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setWireTransferDialog(true);
                  setShowWireBankDetails(false);
                }}
              >
                Wire Transfer
              </Button>
              {stripePublishableKey && (
                <>
                  <Button
                    variant="contained"
                    startIcon={<CreditCardIcon />}
                    onClick={() => setOnlinePaymentDialog(true)}
                  >
                    Online Payment
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<SettingsIcon />}
                    onClick={() => navigate(`/firms/${firmId}/autopay`)}
                  >
                    Auto-Pay Settings
                  </Button>
                </>
              )}
            </Box>
          )}
        </Box>
        
        {balance && (
          <Box>
            <Typography variant="h4" color="primary">
              {balance.currency} {parseFloat(balance.balance).toFixed(2)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {balance.firm_name}
            </Typography>
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 4 }}>
        <Typography variant="h6" gutterBottom>
          Transaction History
        </Typography>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Balance Before</TableCell>
                <TableCell>Balance After</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No transactions found
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell>
                      {new Date(transaction.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={transaction.type}
                        color={getTypeColor(transaction.type) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {transaction.currency} {parseFloat(transaction.amount).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      {transaction.currency} {parseFloat(transaction.balance_before).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      {transaction.currency} {parseFloat(transaction.balance_after).toFixed(2)}
                    </TableCell>
                    <TableCell>{transaction.created_by?.name ?? transaction.createdBy?.name ?? '—'}</TableCell>
                    <TableCell>{transaction.description}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
            />
          </Box>
        )}
      </Paper>

      {/* Cash Deposit Dialog */}
      <Dialog open={depositDialog} onClose={() => !submitting && setDepositDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Cash Deposit</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Amount"
            type="number"
            value={depositAmount}
            onChange={(e) => setDepositAmount(e.target.value)}
            sx={{ mt: 2 }}
            inputProps={{ min: 0.01, step: 0.01 }}
            required
          />
          <TextField
            fullWidth
            label="Description"
            value={depositDescription}
            onChange={(e) => setDepositDescription(e.target.value)}
            sx={{ mt: 2 }}
            multiline
            rows={3}
            placeholder="Optional description for this transaction"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDepositDialog(false)} disabled={submitting}>Cancel</Button>
          <Button
            onClick={handleAddDeposit}
            variant="contained"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : 'Add Deposit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Online Payment Dialog */}
      {stripePublishableKey && (
        <Dialog 
          open={onlinePaymentDialog} 
          onClose={() => {
            if (!submitting) {
              setOnlinePaymentDialog(false);
              setSelectedPaymentMethodId(null);
              setUseNewCard(false);
              setShowWireBankDetails(false);
            }
          }}
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle>Online Payment</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={onlinePaymentAmount}
              onChange={(e) => setOnlinePaymentAmount(e.target.value)}
              sx={{ mt: 2 }}
              inputProps={{ min: 0.01, step: 0.01 }}
              required
            />
            <TextField
              fullWidth
              label="Description"
              value={onlinePaymentDescription}
              onChange={(e) => setOnlinePaymentDescription(e.target.value)}
              sx={{ mt: 2 }}
              multiline
              rows={3}
              placeholder="Optional description for this transaction"
            />
            <FormControl fullWidth sx={{ mt: 2 }} disabled={submitting}>
              <InputLabel id="online-payment-method-label">Payment Method</InputLabel>
              <Select
                labelId="online-payment-method-label"
                value="card"
                label="Payment Method"
                disabled
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      zIndex: 1301,
                    },
                  },
                }}
              >
                <MenuItem value="card">Credit/Debit Card</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ mt: 3 }}>
                {savedPaymentMethods.length > 0 && (
                  <>
                    <Typography variant="subtitle2" gutterBottom>
                      Select Payment Method
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                      {savedPaymentMethods.map((method) => (
                        <Card
                          key={method.id}
                          sx={{
                            cursor: 'pointer',
                            border: selectedPaymentMethodId === method.stripe_payment_method_id ? 2 : 1,
                            borderColor: selectedPaymentMethodId === method.stripe_payment_method_id ? 'primary.main' : 'divider',
                            '&:hover': {
                              borderColor: 'primary.main',
                            },
                          }}
                          onClick={() => {
                            setSelectedPaymentMethodId(method.stripe_payment_method_id);
                            setUseNewCard(false);
                          }}
                        >
                          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box>
                                <Typography variant="body1">
                                  <CreditCardIcon sx={{ fontSize: 20, verticalAlign: 'middle', mr: 1 }} />
                                  ****{method.card_last4}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {method.card_brand?.toUpperCase()} • Expires {String(method.card_exp_month).padStart(2, '0')}/{method.card_exp_year}
                                </Typography>
                              </Box>
                              {method.is_default && (
                                <Chip label="Default" color="primary" size="small" />
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                      <Card
                        sx={{
                          cursor: 'pointer',
                          border: useNewCard ? 2 : 1,
                          borderColor: useNewCard ? 'primary.main' : 'divider',
                          '&:hover': {
                            borderColor: 'primary.main',
                          },
                        }}
                        onClick={() => {
                          setUseNewCard(true);
                          setSelectedPaymentMethodId(null);
                        }}
                      >
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Typography variant="body1">
                            <AddIcon sx={{ fontSize: 20, verticalAlign: 'middle', mr: 1 }} />
                            Use a new card
                          </Typography>
                        </CardContent>
                      </Card>
                    </Box>
                  </>
                )}
                
                {savedPaymentMethods.length === 0 && (
                  <Box>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      No saved payment methods. You can add a card in Auto-Pay Settings or enter a new card to proceed.
                    </Alert>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: useNewCard ? 2 : 1,
                        borderColor: useNewCard ? 'primary.main' : 'divider',
                        '&:hover': {
                          borderColor: 'primary.main',
                        },
                      }}
                      onClick={() => {
                        setUseNewCard(true);
                        setSelectedPaymentMethodId(null);
                      }}
                    >
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography variant="body1">
                          <AddIcon sx={{ fontSize: 20, verticalAlign: 'middle', mr: 1 }} />
                          Enter a new card
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                )}
              </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setOnlinePaymentDialog(false);
              setSelectedPaymentMethodId(null);
              setUseNewCard(false);
              setShowWireBankDetails(false);
            }} disabled={submitting}>Cancel</Button>
            <Button
              onClick={handleOnlinePayment}
              variant="contained"
              disabled={submitting}
              startIcon={<CreditCardIcon />}
            >
              {submitting ? (
                <CircularProgress size={24} />
              ) : (
                useNewCard || savedPaymentMethods.length === 0 ? 'Continue to Payment' : 'Pay Now'
              )}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Wire Transfer Dialog */}
      <Dialog
        open={wireTransferDialog}
        onClose={() => {
          if (!submitting) {
            setWireTransferDialog(false);
            setShowWireBankDetails(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Wire Transfer</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Amount"
            type="number"
            value={onlinePaymentAmount}
            onChange={(e) => setOnlinePaymentAmount(e.target.value)}
            sx={{ mt: 2 }}
            inputProps={{ min: 0.01, step: 0.01 }}
            required
          />
          <TextField
            fullWidth
            label="Description"
            value={onlinePaymentDescription}
            onChange={(e) => setOnlinePaymentDescription(e.target.value)}
            sx={{ mt: 2 }}
            multiline
            rows={3}
            placeholder="Optional description for this transaction"
          />
          {showWireBankDetails && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                Bank Account Details for Wire Transfer
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Recipient Name:</TableCell>
                      <TableCell>{wireBankDetails.recipientName}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>ABA / Routing Number:</TableCell>
                      <TableCell>{wireBankDetails.routingNumber}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Recipient Checking A/C No:</TableCell>
                      <TableCell>{wireBankDetails.accountNumber}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Recipient Bank Name:</TableCell>
                      <TableCell>{wireBankDetails.bankName}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Swift Code:</TableCell>
                      <TableCell>{wireBankDetails.swiftCode}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <Alert severity="info" sx={{ mt: 2 }}>
                Please use the bank account details above to make your wire transfer. After completing the transfer, enter the amount and description above to record the payment.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setWireTransferDialog(false);
              setShowWireBankDetails(false);
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button onClick={handleWireTransfer} variant="contained" disabled={submitting}>
            {submitting ? (
              <CircularProgress size={24} />
            ) : showWireBankDetails ? (
              'Record Wire Payment'
            ) : (
              'View Bank Details'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stripe Payment Dialog */}
      {stripePublishableKey && clientSecret && paymentIntentId && (
        <StripePaymentDialog
          open={stripePaymentDialog}
          onClose={() => setStripePaymentDialog(false)}
          onSuccess={handleStripePaymentSuccess}
          stripePublishableKey={stripePublishableKey}
          clientSecret={clientSecret}
          paymentIntentId={paymentIntentId}
          amount={onlinePaymentAmount}
          firmDetails={firmDetails}
        />
      )}
    </Container>
  );
};

export default ClientFirmWallet;

